import type React from 'react';
import { useEffect, useState } from 'react';
import {
  listAdminProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  type AdminProduct,
} from '../../lib/adminApi';
import { formatCents } from '../../lib/format';
import { AdminPageShell, cls } from './shared';

export function ProductsPage() {
  const [products, setProducts] = useState<AdminProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newSku, setNewSku] = useState('');
  const [newName, setNewName] = useState('');
  const [newPrice, setNewPrice] = useState('');
  const [saving, setSaving] = useState(false);

  function load() {
    setLoading(true);
    listAdminProducts()
      .then((r) => {
        setProducts(r.products);
        setLoading(false);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : 'Failed to load');
        setLoading(false);
      });
  }

  useEffect(load, []);

  async function handleCreate() {
    const cents = Math.round(parseFloat(newPrice) * 100);
    if (isNaN(cents) || cents < 0) {
      alert('Enter a valid price');
      return;
    }
    setSaving(true);
    try {
      await createProduct({ sku: newSku.trim(), name: newName.trim(), unit_price_cents: cents });
      setShowCreate(false);
      setNewSku('');
      setNewName('');
      setNewPrice('');
      load();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Error creating product');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <AdminPageShell title="Products"><p className="text-vari-gray text-sm">Loading…</p></AdminPageShell>;
  if (error) return <AdminPageShell title="Products"><p className="text-red-600 text-sm">{error}</p></AdminPageShell>;

  return (
    <AdminPageShell
      title={`Products (${products.length})`}
      actions={
        <button
          onClick={() => setShowCreate(true)}
          className={cls.btnPrimary}
        >
          + New Product
        </button>
      }
    >
      {showCreate && (
        <div className={cls.formPanel}>
          <p className="text-sm font-medium text-vari-navy mb-3">New Product</p>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div>
              <label className="text-xs text-vari-gray block mb-1">SKU</label>
              <input
                className={cls.input}
                value={newSku}
                onChange={(e) => setNewSku(e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs text-vari-gray block mb-1">Name</label>
              <input
                className={cls.input}
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs text-vari-gray block mb-1">Unit Price ($)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                className={cls.input}
                value={newPrice}
                onChange={(e) => setNewPrice(e.target.value)}
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => void handleCreate()}
              disabled={saving}
              className={cls.btnSmPrimary}
            >
              {saving ? 'Saving…' : 'Create'}
            </button>
            <button onClick={() => setShowCreate(false)} className={cls.btnGhost}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className={cls.tableWrap}>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-vari-soft-gray">
            <tr>
              <th className={cls.th}>SKU</th>
              <th className={cls.th}>Name</th>
              <th className={cls.thRight}>Unit Price</th>
              <th className={cls.th}>Used in</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {products.map((p, i) => (
              <ProductRow
                key={p.id}
                product={p}
                striped={i % 2 === 1}
                onDelete={() => void handleDeleteProduct(p)}
                onSaved={load}
              />
            ))}
            {products.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-vari-gray text-sm">
                  No products yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </AdminPageShell>
  );

  async function handleDeleteProduct(p: AdminProduct) {
    if (!confirm(`Delete product "${p.name}" (${p.sku})?`)) return;
    try {
      await deleteProduct(p.id);
      load();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Error deleting product');
    }
  }
}

function ProductRow({
  product,
  striped,
  onDelete,
  onSaved,
}: {
  product: AdminProduct;
  striped: boolean;
  onDelete: () => void;
  onSaved: () => void;
}) {
  const [editName, setEditName] = useState(product.name);
  const [editPrice, setEditPrice] = useState((product.unitPriceCents / 100).toFixed(2));
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  async function save() {
    const cents = Math.round(parseFloat(editPrice) * 100);
    if (isNaN(cents) || cents < 0) {
      alert('Invalid price');
      return;
    }
    setSaving(true);
    try {
      await updateProduct(product.id, { name: editName, unit_price_cents: cents });
      setEditing(false);
      onSaved();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <tr className={cls.tr(striped)}>
      <td className={cls.tdMono}>{product.sku}</td>
      <td className={cls.td}>
        {editing ? (
          <input
            className="border border-vari-soft-gray rounded px-2 py-0.5 text-sm w-48"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void save(); if (e.key === 'Escape') setEditing(false); }}
            autoFocus
          />
        ) : (
          <span className="cursor-pointer hover:text-vari-navy hover:underline" onClick={() => setEditing(true)}>
            {product.name}
          </span>
        )}
      </td>
      <td className={cls.tdRight}>
        {editing ? (
          <input
            type="number"
            step="0.01"
            min="0"
            className="border border-vari-soft-gray rounded px-2 py-0.5 text-sm w-24 text-right"
            value={editPrice}
            onChange={(e) => setEditPrice(e.target.value)}
          />
        ) : (
          <span className="cursor-pointer hover:text-vari-navy hover:underline" onClick={() => setEditing(true)}>
            {formatCents(product.unitPriceCents)}
          </span>
        )}
      </td>
      <td className={`${cls.td} text-vari-gray`}>{product.usageCount} layouts</td>
      <td className={`${cls.tdRight} space-x-2`}>
        {editing ? (
          <>
            <button onClick={() => void save()} disabled={saving} className={cls.btnSmPrimary}>
              {saving ? '…' : 'Save'}
            </button>
            <button onClick={() => setEditing(false)} className={cls.btnGhost}>Cancel</button>
          </>
        ) : (
          <>
            <button onClick={() => setEditing(true)} className={cls.btnGhost}>Edit</button>
            {confirmDelete ? (
              <>
                <button onClick={onDelete} className={cls.btnConfirm}>Confirm?</button>
                <button onClick={() => setConfirmDelete(false)} className={cls.btnGhost}>Cancel</button>
              </>
            ) : (
              <button
                onClick={() => setConfirmDelete(true)}
                disabled={product.usageCount > 0}
                className={`${cls.btnDanger} disabled:opacity-30 disabled:cursor-not-allowed`}
                title={product.usageCount > 0 ? `Used in ${product.usageCount} layouts` : undefined}
              >
                Delete
              </button>
            )}
          </>
        )}
      </td>
    </tr>
  );
}
